import { useUrlMetadataQuery } from '@modules/url-metadata/private/hooks/useUrlMetadataQuery';
import { Link, Typography, styled } from '@mui/material';
import { Stack } from '@mui/system';
import { SearchSource } from 'src/@types/ai';
import { UrlMetadataResult } from 'src/@types/url';
import { Loading } from 'src/components/Loading/Loading';
import Iconify from 'src/components/iconify';

const REGEX =
  /([a-zA-Z0-9]+:\/\/)?([a-zA-Z0-9]+:[a-zA-Z0-9]+@)?([a-zA-Z0-9.-]+\.[A-Za-z]{2,4})(:[0-9]+)?(\/.*)?/;
const url = (content: string) =>
  content.replace(/(<([^>]+)>)/gi, '').match(REGEX)?.[0] || '';

export const AISearchEmbedInner = ({
  metadata,
  url,
  title,
}: { metadata?: UrlMetadataResult; url: string; title: string }) => {
  return (
    <StyledLink href={url} target="_blank">
      <Stack gap={0.5} flexGrow={1} overflow="hidden">
        <Stack direction="row" alignItems="center" gap={0.5} overflow="hidden">
          {metadata?.favicon ? (
            <img
              src={metadata.favicon}
              alt="website icon"
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
              }}
            />
          ) : (
            <Iconify icon="tabler:world" flexShrink={0} width={16} />
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {metadata?.name || title}
          </Typography>
        </Stack>
        <Typography variant="caption" fontWeight={500} whiteSpace="wrap">
          {metadata?.title || title}
        </Typography>
      </Stack>
    </StyledLink>
  );
};

export const AISearchEmbed = ({
  url,
  title,
}: { url: string; title: string }) => {
  const { data, isLoading } = useUrlMetadataQuery(url);

  if (isLoading) return <Loading />;

  if (!isLoading && !data)
    return <AISearchEmbedInner title={title} url={url} />;

  return (
    <AISearchEmbedInner
      metadata={data as UrlMetadataResult}
      title={title}
      url={url}
    />
  );
};

export const AISearchItem = ({ source }: { source: SearchSource }) => {
  let validUrl = url(source.url);

  if (!validUrl.includes('@') && !validUrl.startsWith('http')) {
    validUrl = `https://${url}`;
  }

  return <AISearchEmbed url={validUrl} title={source.title} />;
};

const StyledLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
  overflow: 'hidden',
  '&:hover': {
    textDecoration: 'none',
  },
}));
